import {
  Form,
  useDialogHeadingId,
  Button,
  AnimatedSpinner,
  IconSearch,
} from '@madpaws/design-system';
import classNames from 'classnames';
import { Formik } from 'formik';
import React from 'react';

import { expandedSearchWidgetValidationSchema } from '~/common/components/SearchBarWidget/MobileSearchWidgetWrapper/utils';

import styles from './ExpandedSearchFiltersForm.module.css';
import { FormGroup } from './FormGroup';
import { RWB_SERVICE_NAMES, SEARCH_FILTERS_FORM_ID } from '../constants';
import { ExpandedBookingTypeField } from '../ui/BookingType/ExpandedBookingTypeField';
import { DateSelector } from '../ui/DateSelector/DateSelector';
import { ErrorBoundary } from '../ui/ErrorBoundary/ErrorBoundary';
import { ExpandedPetTypeField } from '../ui/ExpandedPetTypeField/ExpandedPetTypeField';
import { mapPetArrayToPetTypes } from '../ui/ExpandedPetTypeField/utils';
import { ExpandedServiceTypeField } from '../ui/ExpandedServiceType/ExpandedServiceTypeField';
import { LocationField } from '../ui/Location/LocationField';

import type { ReactElement } from 'react';
import type { SearchFilters } from '~/common/types/search';

type Props = {
  currentValues: SearchFilters;
  onFilterSubmit: (searchFilters: SearchFilters) => void;
};

const ExpandedSearchFiltersForm = ({ currentValues, onFilterSubmit }: Props): ReactElement => {
  const dialogHeadingId = useDialogHeadingId();

  const handleSubmit = (values: SearchFilters): void => {
    const petTypes = mapPetArrayToPetTypes(values?.pets);
    onFilterSubmit({
      ...currentValues,
      bookingType: values.bookingType,
      chronology: {
        startDate: values.chronology.startDate,
        endDate: values.chronology.endDate,
        scheduledDates: values.chronology.scheduledDates,
        weekDays: values.chronology.weekDays,
        rwbStartDate: values.chronology.rwbStartDate,
      },
      location: values.location,
      petTypes,
      service: {
        type: values.service.type,
        quantity: 0,
      },
    });
  };

  return (
    <Formik
      initialValues={{
        ...currentValues,
        pets: [{ type: '' }],
      }}
      onSubmit={handleSubmit}
      validateOnChange={false}
      validationSchema={expandedSearchWidgetValidationSchema}
    >
      {(props): ReactElement => (
        <div className={styles.root}>
          <Form
            hasHtml5Validation={false}
            hasTighterSpacing
            id={SEARCH_FILTERS_FORM_ID}
            isGridRowGapDisabled={false}
            label={{ id: dialogHeadingId }}
          >
            <FormGroup>
              <ErrorBoundary fieldName="pets" isExpanded>
                <ExpandedPetTypeField />
              </ErrorBoundary>
            </FormGroup>

            <FormGroup>
              <ErrorBoundary fieldName="service" isExpanded>
                <ExpandedServiceTypeField />
              </ErrorBoundary>

              <div className={styles.footerRow}>
                <div>
                  <ErrorBoundary fieldName="location" isExpanded>
                    <LocationField
                      isLabelVisuallyHidden
                      label="Nearby"
                      placeholder="Add your street address or suburb"
                    />
                  </ErrorBoundary>
                  <div
                    className={classNames({
                      [styles.dateRowRWB]: RWB_SERVICE_NAMES.includes(props.values.service.type),
                      [styles.dateRow]: !RWB_SERVICE_NAMES.includes(props.values.service.type),
                    })}
                  >
                    <ExpandedBookingTypeField />

                    <ErrorBoundary fieldName="chronology" isExpanded>
                      <DateSelector
                        dateFormat="fancy"
                        isCalendarIconHidden={false}
                        isFromExpandedSearchBar
                        isLabelVisuallyHidden
                      />
                    </ErrorBoundary>
                  </div>
                </div>
                <div className={styles.searchButton}>
                  <Button
                    iconLeading={
                      props.isSubmitting ? (
                        <AnimatedSpinner size="small" />
                      ) : (
                        <IconSearch size="small" />
                      )
                    }
                    isDisabled={props.isSubmitting}
                    isFullBleed
                    label="Search for a sitter"
                    onClick={(): Promise<void> => props.submitForm()}
                    size="large1x"
                    variant="primary"
                  />
                </div>
              </div>
            </FormGroup>
          </Form>
        </div>
      )}
    </Formik>
  );
};

export { ExpandedSearchFiltersForm };
